import { separateWords } from '@acme/auth'
import modules, { naoListadas } from '@acme/auth/modules'
import { AppAbilities } from '@acme/auth/types'
import React from 'react'
import { Menu } from 'routes/types'
import { useAuth } from './useAuth'

const newTab = ['/ajuda/atendimento-online', '/ajuda/manual']

export function useBackendMenu() {
    const { permissions } = useAuth()

    if (!permissions) return []

    return Object.entries(modules)
        .map(([key, value]) => ({
            label: key,
            subitens: Object.entries(value).map(([key, value]) => ({
                label: key,
                redirectTo: value.href,
                newTab: newTab.includes(value.href),
            })),
        }))
        .map(item => ({
            ...item,
            subitens: item.subitens
                .filter(it => permissions.can('read', it.label as any))
                .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .map(item => ({
            ...item,
            label: separateWords(item.label),
            subitens: item.subitens.map(it => ({
                ...it,
                label: separateWords(it.label),
            })),
        }))
        .filter(item => item.subitens.length) as Menu
}

export function hasPathPermission(type: AppAbilities[0], path: string) {
    const { permissions } = useAuth()
    const routes = Object.values(modules).flatMap(i => Object.values(i))
    const rotasNaoListadas = Object.values(naoListadas)

    return [...routes, ...rotasNaoListadas].some(it => {
        const { href, label } = it
        return (
            String(path).startsWith(href) && permissions.can(type, label as any)
        )
    })
}

/** Hook que verifica se o usuário possui determinada permissão no sistema
 *
 * As regras dos hook do React devem ser seguidas ao utilizar este hook.
 *
 * @example
 * ```tsx
 * // Verifica se o usuário possui permissão
 * const canUpdate = hasPermission(['update', 'Empresas'])
 *
 * // Renderiza um componente se o usuário possuir permissão
 * const EditButton = () => (
 *   {hasPermission(['update', 'Empresas'], () => (
 *     <button>Editar Empresa</button>
 *   ))}
 * )
 */
export function hasPermission(perm: AppAbilities): boolean
export function hasPermission(
    perm: AppAbilities,
    truth: () => React.ReactNode,
): React.ReactNode
export function hasPermission(
    perm: AppAbilities,
    truth: () => React.ReactNode,
    falsy: () => React.ReactNode,
): React.ReactNode
export function hasPermission(
    perm: AppAbilities,
    truth?: () => React.ReactNode,
    falsy?: () => React.ReactNode,
) {
    const { permissions } = useAuth()
    if (!truth && !falsy) {
        return permissions.can(...(perm as any))
    }

    falsy ??= () => null
    return permissions.can(...(perm as any)) ? truth?.() : falsy()
}
